import React, { useState } from 'react'

const PARTICULIER_SERVICES = [
  { label: "Totaal renovatie", value: "Totaal renovatie" },
  { label: "Trappen", value: "Trappen" },
  { label: "Aanbouw", value: "Aanbouw" },
  { label: "Vloeren", value: "Vloeren" },
  { label: "Dakopbouw", value: "Dakopbouw" },
  { label: "Badkamers en Toiletten", value: "Badkamers en Toiletten" },
  { label: "Dakkapel", value: "Dakkapel" },
  { label: "Keukens", value: "Keukens" },
  { label: "Veranda", value: "Veranda" },
  { label: "Stalen taatsdeur", value: "Stalen taatsdeur" },
  { label: "Decoratieve werkzaamheden", value: "Decoratieve werkzaamheden" },
  { label: "Tuinen", value: "Tuinen" },
  { label: "Zonnepanelen", value: "Zonnepanelen" },
  { label: "Thuisbatterij", value: "Thuisbatterij" },
  { label: "Laadpaal", value: "Laadpaal" },
  { label: "Ventilatie", value: "Ventilatie" },
  { label: "Overige verbouwingen", value: "Overige verbouwingen" },
]

const ZAKELIJK_SERVICES = [
  { label: "Praktijkruimte", value: "Praktijkruimte" },
  { label: "Salons", value: "Salons" },
  { label: "Ziekenhuis", value: "Ziekenhuis" },
  { label: "Horeca", value: "Horeca" },
  { label: "Overige verbouwingen", value: "Overige verbouwingen" },
  { label: "Winkelruimte", value: "Winkelruimte" },
  { label: "Kantoorruimte", value: "Kantoorruimte" },
  { label: "Hotel", value: "Hotel" },
]

const STEPS = ['type', 'services', 'address', 'contact', 'done']

// Enhanced Conversions expects a normalised email and an E.164 phone number (+31...)
const normalizeEmail = (email) => (email || '').trim().toLowerCase()
const toE164 = (phone) => {
  let digits = (phone || '').replace(/[^\d+]/g, '')
  if (digits.startsWith('00')) digits = '+' + digits.slice(2)
  if (digits.startsWith('+')) return digits
  if (digits.startsWith('31')) return '+' + digits
  if (digits.startsWith('0')) return '+31' + digits.slice(1)
  return digits
}

export default function QuoteForm({ formTitle, formSubtitle, usps, uspsTitle, buttonText, footerText, initialService, formName = 'offerte' }) {
  // A preselected service (service page, or ?dienst= on the homepage) skips the type step
  const [step, setStep] = useState(initialService ? 1 : 0)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const [serverError, setServerError] = useState(null)

  const [form, setForm] = useState({
    client_type: initialService ? 'particulier' : '',
    services: initialService ? [initialService] : [],
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    postcode: '',
    house_number: '',
    addition: '',
    description: '',
    preferred_start: '',
  })

  const currentStepName = STEPS[step]

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
    setErrors(prev => ({ ...prev, [field]: null }))
  }

  const toggleService = (service) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }))
    setErrors(prev => ({ ...prev, services: null }))
  }

  const validateStep = () => {
    const newErrors = {}

    if (currentStepName === 'type') {
      if (!form.client_type) newErrors.client_type = 'Kies een optie'
    }

    if (currentStepName === 'services') {
      if (form.services.length === 0) newErrors.services = 'Selecteer minimaal één dienst'
    }

    if (currentStepName === 'address') {
      if (!form.postcode.trim()) newErrors.postcode = 'Verplicht'
      if (!form.house_number.trim()) newErrors.house_number = 'Verplicht'
    }

    if (currentStepName === 'contact') {
      if (!form.first_name.trim()) newErrors.first_name = 'Verplicht'
      if (!form.last_name.trim()) newErrors.last_name = 'Verplicht'
      if (!form.email.trim()) newErrors.email = 'Verplicht'
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Ongeldig e-mailadres'
      if (!form.phone.trim()) newErrors.phone = 'Verplicht'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const next = () => {
    if (validateStep()) setStep(s => s + 1)
  }

  const prev = () => setStep(s => Math.max(0, s - 1))

  const submit = async () => {
    if (!validateStep()) return

    setSubmitting(true)
    setServerError(null)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const res = await fetch('/quote_requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify({
          quote_request: {
            ...form,
            services: JSON.stringify(form.services),
          }
        }),
      })

      const data = await res.json()

      if (res.ok) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'generate_lead',
          form_name: formName,
          page_path: window.location.pathname,
          client_type: form.client_type,
          services: form.services,
          preferred_start: form.preferred_start,
          user_data: { email: normalizeEmail(form.email), phone_number: toE164(form.phone) },
        })
        setStep(STEPS.indexOf('done'))
      } else {
        setServerError(data.errors?.join(', ') || 'Er ging iets mis.')
      }
    } catch {
      setServerError('Verbindingsfout. Probeer het opnieuw.')
    } finally {
      setSubmitting(false)
    }
  }

  const progressWidth = `${((step) / (STEPS.length - 2)) * 100}%`

  // --- RENDER HELPERS ---

  const inputClass = (field) =>
    `w-full px-4 py-3 text-sm text-gray-700 bg-[#E9ECEB] border ${errors[field] ? 'border-red-400' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#047970]/30 focus:border-[#047970] placeholder-gray-400 transition-colors`

  const renderError = (field) =>
    errors[field] ? <p className="text-red-500 text-xs mt-1 ml-1">{errors[field]}</p> : null

  const renderStepType = () => (
    <div className="space-y-3 mt-6">
      <p className="text-sm font-medium text-gray-600 mb-4">Waarvoor zoekt u een aannemer?</p>
      <div className="grid grid-cols-2 gap-3">
        {['particulier', 'zakelijk'].map(type => (
          <button
            key={type}
            type="button"
            onClick={() => {
              update('client_type', type)
              update('services', [])
              setStep(1)
            }}
            className="text-center px-4 py-5 rounded-xl border-2 border-gray-200 bg-[#E9ECEB] hover:border-[#047970] hover:bg-[#047970]/5 transition-all cursor-pointer"
          >
            <div className="flex flex-col items-center gap-2">
              <span className="text-2xl">{type === 'particulier' ? '🏠' : '🏢'}</span>
              <span className="font-semibold text-[#222222] capitalize">{type}</span>
              <p className="text-xs text-gray-500">
                {type === 'particulier' ? 'Woning of privéproject' : 'Bedrijfspand of commercieel'}
              </p>
            </div>
          </button>
        ))}
      </div>
      {renderError('client_type')}
    </div>
  )

  const renderStepServices = () => {
    const services = form.client_type === 'zakelijk' ? ZAKELIJK_SERVICES : PARTICULIER_SERVICES

    return (
      <div className="space-y-3">
        <p className="text-sm font-medium text-gray-600 mb-2">Selecteer de gewenste dienst(en)</p>
        <div className="grid grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-1">
          {services.map(({ label, value }) => {
            const checked = form.services.includes(value)
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleService(value)}
                className={`text-left px-3 py-2.5 rounded-lg border transition-all text-sm ${
                  checked
                    ? 'border-[#047970] bg-[#047970]/5 text-[#047970] font-medium'
                    : 'border-gray-200 bg-[#E9ECEB] text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center text-[10px] ${
                    checked ? 'bg-[#047970] border-[#047970] text-white' : 'border-gray-400'
                  }`}>
                    {checked && '✓'}
                  </span>
                  <span className="leading-tight">{label}</span>
                </div>
              </button>
            )
          })}
        </div>
        {renderError('services')}
      </div>
    )
  }

  const renderStepAddress = () => (
    <div className="space-y-3">
      <p className="text-sm font-medium text-gray-600 mb-2">Waar moeten de werkzaamheden plaatsvinden?</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">Postcode *</label>
          <input type="text" placeholder="1234 AB" value={form.postcode}
            onChange={e => update('postcode', e.target.value)} className={inputClass('postcode')} />
          {renderError('postcode')}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">Huisnummer *</label>
          <input type="text" placeholder="12" value={form.house_number}
            onChange={e => update('house_number', e.target.value)} className={inputClass('house_number')} />
          {renderError('house_number')}
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">Toevoeging</label>
        <input type="text" placeholder="A (optioneel)" value={form.addition}
          onChange={e => update('addition', e.target.value)} className={inputClass('addition')} />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">Gewenste startdatum</label>
        <select value={form.preferred_start} onChange={e => update('preferred_start', e.target.value)}
          className={inputClass('preferred_start')}>
          <option value="">Selecteer...</option>
          <option value="Zo snel mogelijk">Zo snel mogelijk</option>
          <option value="Binnen 1 maand">Binnen 1 maand</option>
          <option value="Binnen 3 maanden">Binnen 3 maanden</option>
          <option value="Binnen 6 maanden">Binnen 6 maanden</option>
          <option value="Nog onbekend">Nog onbekend</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">Omschrijving (optioneel)</label>
        <textarea rows={3} placeholder="Beschrijf kort uw project..." value={form.description}
          onChange={e => update('description', e.target.value)} className={inputClass('description')} />
      </div>
    </div>
  )

  const renderStepContact = () => (
    <div className="space-y-3">
      <p className="text-sm font-medium text-gray-600 mb-2">Uw contactgegevens</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">Voornaam *</label>
          <input type="text" placeholder="Jan" value={form.first_name}
            onChange={e => update('first_name', e.target.value)} className={inputClass('first_name')} />
          {renderError('first_name')}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">Achternaam *</label>
          <input type="text" placeholder="de Vries" value={form.last_name}
            onChange={e => update('last_name', e.target.value)} className={inputClass('last_name')} />
          {renderError('last_name')}
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">E-mailadres *</label>
        <input type="email" placeholder="jan@voorbeeld.nl" value={form.email}
          onChange={e => update('email', e.target.value)} className={inputClass('email')} />
        {renderError('email')}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">Telefoonnummer *</label>
        <input type="tel" placeholder="06 12345678" value={form.phone}
          onChange={e => update('phone', e.target.value)} className={inputClass('phone')} />
        {renderError('phone')}
      </div>
    </div>
  )

  const renderDone = () => (
    <div className="text-center py-6 space-y-4">
      <div className="mx-auto w-16 h-16 bg-[#047970]/10 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-[#047970]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-xl font-bold text-[#222222]">Aanvraag ontvangen!</h3>
      <p className="text-sm text-gray-500 max-w-xs mx-auto">
        Bedankt voor uw aanvraag. Wij nemen binnen 24 uur contact met u op.
      </p>
    </div>
  )

  const renderStep = () => {
    switch (currentStepName) {
      case 'type': return renderStepType()
      case 'services': return renderStepServices()
      case 'address': return renderStepAddress()
      case 'contact': return renderStepContact()
      case 'done': return renderDone()
    }
  }

  const stepLabels = ['Type', 'Diensten', 'Adres', 'Contact']

  return (
    <div className="w-full bg-white rounded-2xl p-7 sm:p-9 shadow-2xl shadow-black/10 ring-1 ring-[#E9ECEB]">
      {currentStepName !== 'done' && (
        <>
          <h2 className="text-[#222222] text-xl sm:text-2xl font-bold leading-snug">
            {formTitle || 'Benieuwd wat het gaat kosten?'}
          </h2>
          <p className="mt-2 text-gray-500 text-sm leading-relaxed">
            {formSubtitle || 'Je ontvangt van ons een persoonlijk voorstel direct in je mailbox.'}
          </p>

          {/* USP checkmarks */}
          {usps && usps.length > 0 && (
            <div className="mt-5 bg-[#E9ECEB] rounded-xl p-4">
              <p className="text-[#222222] font-semibold text-sm mb-3">{uspsTitle || 'Kies voor slim verbouwen'}</p>
              <ul className="space-y-2.5">
                {usps.map((usp, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 bg-[#047970] rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-xs"></i>
                    </span>
                    <span className="text-gray-700 text-sm font-medium">{usp}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Progress bar — hidden on first step */}
          <div className={`mt-6 mb-5${step === 0 ? ' hidden' : ''}`}>
            <div className="flex justify-between mb-2">
              {stepLabels.map((label, i) => (
                <span key={i} className={`text-[10px] font-semibold uppercase tracking-wider ${
                  i <= step ? 'text-[#047970]' : 'text-gray-400'
                }`}>{label}</span>
              ))}
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#047970] rounded-full transition-all duration-500 ease-out"
                style={{ width: progressWidth }}
              />
            </div>
          </div>
        </>
      )}

      {/* Step content */}
      {renderStep()}

      {serverError && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
          {serverError}
        </div>
      )}

      {/* Navigation buttons — hidden on type step */}
      {currentStepName !== 'done' && step > 0 && (
        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={prev}
            className="px-5 py-3 text-sm font-medium text-gray-600 bg-[#E9ECEB] rounded-xl hover:bg-gray-200 transition-colors"
          >
            Terug
          </button>
          {currentStepName !== 'contact' ? (
            <button
              type="button"
              onClick={next}
              className="flex-1 bg-[#047970] hover:bg-[#059085] text-white font-semibold text-[15px] py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-[#047970]/20"
            >
              Volgende
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="flex-1 bg-[#047970] hover:bg-[#059085] text-white font-semibold text-[15px] py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-[#047970]/20 disabled:opacity-60"
            >
              {submitting ? 'Verzenden...' : (buttonText || 'Verstuur aanvraag')}
            </button>
          )}
        </div>
      )}

      {currentStepName !== 'done' && (
        <p className="mt-4 text-center text-gray-400 text-xs">
          {footerText || 'Binnen 1 minuut geregeld!'}
        </p>
      )}
    </div>
  )
}
